import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { StatusMark, type StatusTone } from '../../../components/StatusMark';
import { useTranslation } from '../../../i18n';
import { useSelectedShop } from '../layout/selectedShopContext';
import { getCollaborationPage } from '../../../store/request';
import type { CollaborationStatus, CollaborationSubmitStatus, TargetCollaboration } from '../../../types/collaboration';
import { AddInviteProductModal } from './AddInviteProductModal';
import { AddInviteTalentModal } from './AddInviteTalentModal';
import { CreateTargetInviteModal } from './CreateTargetInviteModal';
import { InviteDetailModal } from './InviteDetailModal';
import { MergeInviteGroupModal } from './MergeInviteGroupModal';
import { CleanupInviteModal } from './CleanupInviteModal';
import { RemoveInviteTalentModal } from './RemoveInviteTalentModal';

type StatusFilter = 'all' | CollaborationStatus;

const statusFilters: StatusFilter[] = ['all', 'ONGOING', 'EXPIRING', 'VALID', 'CANCELING', 'COMPLETED'];

const submitStatusOptions: CollaborationSubmitStatus[] = ['SUBMITTING', 'SUCCESS', 'FAILED', 'UNKNOWN'];

const editableStatuses = new Set(['ONGOING', 'EXPIRING', 'VALID']);

function formatCount(value?: number) {
  if (value == null || !Number.isFinite(value)) return '—';
  return String(value);
}

function formatTime(value?: string) {
  if (!value) return '—';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  return parsed.format('YYYY-MM-DD HH:mm:ss');
}

function rowKeyOf(row: TargetCollaboration) {
  if (row.id != null) return String(row.id);
  return row.targetCollaborationId || row.name || '';
}

function invitationStatusTone(status?: string): StatusTone | '' {
  if (status === 'ONGOING' || status === 'VALID' || status === 'SUCCESS') return 'success';
  if (status === 'EXPIRING' || status === 'SUBMITTING' || status === 'UNKNOWN') return 'pending';
  if (status === 'COMPLETED') return 'muted';
  if (!status) return '';
  return 'danger';
}

function statusTag(status: string | undefined, label: string) {
  if (!status && !label) return '—';
  return <StatusMark tone={invitationStatusTone(status)}>{label || '—'}</StatusMark>;
}
function statusLabelOf(status: string | undefined, translate: (key: string) => string) {
  if (status === 'ONGOING') return translate('invitation.filter_ongoing');
  if (status === 'EXPIRING') return translate('invitation.status_expiring');
  if (status === 'VALID') return translate('invitation.status_valid');
  if (status === 'CANCELING') return translate('invitation.status_canceling');
  if (status === 'COMPLETED') return translate('invitation.status_completed');
  if (status === 'SUBMITTING') return translate('invitation.submit_submitting');
  if (status === 'SUCCESS') return translate('invitation.submit_success');
  if (status === 'FAILED') return translate('invitation.submit_failed');
  if (status === 'UNKNOWN') return translate('invitation.submit_unknown');
  return status || '';
}

export const Invitation = observer(() => {
  const { t } = useTranslation();
  const { shopId } = useSelectedShop();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [nameInput, setNameInput] = useState('');
  const [nameQuery, setNameQuery] = useState('');
  const [submitStatus, setSubmitStatus] = useState<CollaborationSubmitStatus | ''>('');
  const [endTimeOrder, setEndTimeOrder] = useState<'asc' | 'desc'>('desc');
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [records, setRecords] = useState<TargetCollaboration[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [addTalentOpen, setAddTalentOpen] = useState(false);
  const [talentInviteIds, setTalentInviteIds] = useState<number[]>([]);
  const [removeTalentOpen, setRemoveTalentOpen] = useState(false);
  const [removeInviteIds, setRemoveInviteIds] = useState<number[]>([]);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [productInviteIds, setProductInviteIds] = useState<number[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState<number>();
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeInvitations, setMergeInvitations] = useState<TargetCollaboration[]>([]);
  const [listVersion, setListVersion] = useState(0);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupInviteIds, setCleanupInviteIds] = useState<number[]>([]);
  const [createOpen, setCreateOpen] = useState(false);

  const refreshList = () => {
    setSelectedRowKeys([]);
    setListVersion((version) => version + 1);
  };

  useEffect(() => {
    if (!shopId) {
      setRecords([]);
      setTotal(0);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const name = nameQuery.trim();
    getCollaborationPage({
      pageNum,
      pageSize,
      shopId,
      endTimeOrder,
      ...(name ? { name } : {}),
      ...(submitStatus ? { submitStatus } : {}),
      ...(statusFilter === 'all' ? {} : { collaborationStatus: statusFilter })
    })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          message.error(res.message || t('invitation.load_failed'));
          setRecords([]);
          setTotal(0);
          return;
        }
        const page = res.content;
        const list = page?.records;
        setRecords(Array.isArray(list) ? list : []);
        setTotal(typeof page?.total === 'number' ? page.total : 0);
      })
      .catch(() => {
        if (!cancelled) message.error(t('invitation.load_failed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shopId, pageNum, pageSize, statusFilter, nameQuery, submitStatus, endTimeOrder, listVersion, t]);

  const openInviteDetail = (row: TargetCollaboration) => {
    if (!shopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    if (row.id == null) {
      message.warning(t('invitation.detail_load_failed'));
      return;
    }
    setDetailId(row.id);
    setDetailOpen(true);
  };
  const closeInviteDetail = () => {
    setDetailOpen(false);
    setDetailId(undefined);
  };
  const filterLabels = useMemo(
    () => ({
      all: t('invitation.filter_all'),
      ONGOING: t('invitation.filter_ongoing'),
      EXPIRING: t('invitation.status_expiring'),
      VALID: t('invitation.status_valid'),
      CANCELING: t('invitation.status_canceling'),
      COMPLETED: t('invitation.status_completed')
    }),
    [t]
  );
  const inviteIdsOf = (row?: TargetCollaboration) => {
    const source = row ? [row] : records.filter((item) => selectedRowKeys.map(String).includes(rowKeyOf(item)));
    return Array.from(new Set(source.map((item) => item.id).filter((id): id is number => id != null)));
  };
  const openWithInviteIds = (row: TargetCollaboration | undefined, limitMessage: string) => {
    if (!shopId) {
      message.warning(t('invitation.invite_shop_required'));
      return null;
    }
    const ids = inviteIdsOf(row);
    if (ids.length === 0) {
      message.warning(t('invitation.invite_pick_required'));
      return null;
    }
    if (ids.length > 10) {
      message.warning(limitMessage);
      return null;
    }
    return ids;
  };
  const openAddTalent = (row?: TargetCollaboration) => {
    const ids = openWithInviteIds(row, t('invitation.invite_limit'));
    if (!ids) return;
    setTalentInviteIds(ids);
    setAddTalentOpen(true);
  };
  const openAddProduct = (row?: TargetCollaboration) => {
    const ids = openWithInviteIds(row, t('invitation.add_products_limit'));
    if (!ids) return;
    setProductInviteIds(ids);
    setAddProductOpen(true);
  };
  const openRemoveTalent = (row?: TargetCollaboration) => {
    const ids = openWithInviteIds(row, t('invitation.invite_limit'));

    if (!ids) return;
    setRemoveInviteIds(ids);
    setRemoveTalentOpen(true);
  };
  const openCleanup = () => {
    const ids = openWithInviteIds(undefined, t('invitation.invite_limit'));
    if (!ids) return;
    setCleanupInviteIds(ids);
    setCleanupOpen(true);
  };
  const openMerge = () => {
    if (!shopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    const selectedKeys = new Set(selectedRowKeys.map(String));
    const selected = records.filter((item) => selectedKeys.has(rowKeyOf(item)));
    const mergeable = selected.filter(
      (item) =>
        item.id != null &&
        item.collaborationStatus?.trim().toUpperCase() === 'ONGOING' &&
        !item.updateBlocked
    );
    if (mergeable.length < 2) {
      message.warning(selected.length >= 2 ? t('invitation.too_few') : t('invitation.pick_two'));
      return;
    }
    if (mergeable.length > 10) {
      message.warning(t('invitation.invite_limit'));
      return;
    }
    setMergeInvitations(mergeable);
    setMergeOpen(true);
  };
  const columns: ColumnsType<TargetCollaboration> = useMemo(
    () => [
      {
        title: t('invitation.col_name'),
        key: 'name',
        render: (_, row) => (
          <div className='py-0.5'>
            <div className='font-semibold text-slate-800'>{row.name || '—'}</div>
            {/* <div className='mt-0.5 text-xs text-slate-400'>{row.shopName || '—'}</div> */}
          </div>
        )
      },
      {
        title: t('invitation.col_status'),
        key: 'status',
        render: (_, row) => {
          const platform = row.collaborationStatus;
          const submit = row.submitStatus;
          const primary = platform || submit;
          return (
            <div className='flex flex-col items-start gap-1'>
              {statusTag(primary, statusLabelOf(primary, t) || '—')}
              {platform && submit ? (
                <span className='text-xs text-slate-400'>{statusLabelOf(submit, t)}</span>
              ) : null}
            </div>
          );
        }
      },
      {
        title: t('invitation.col_product'),
        dataIndex: 'productCount',
        align: 'center',
        render: (value?: number) => formatCount(value)
      },
      {
        title: t('invitation.col_deadline'),
        dataIndex: 'endTime',
        sorter: true,
        sortOrder: endTimeOrder === 'asc' ? 'ascend' : 'descend',
        sortDirections: ['descend', 'ascend', 'descend'],
        render: (value?: string) => formatTime(value)
      },
      {
        title: t('invitation.col_added'),
        dataIndex: 'invitedCreatorCount',
        align: 'center',
        render: (value?: number) => formatCount(value)
      },
      {
        title: t('invitation.col_accepted'),
        dataIndex: 'acceptedCreatorCount',
        align: 'center',
        render: (value?: number) => formatCount(value)
      },
      {
        title: t('invitation.col_fulfilled'),
        dataIndex: 'fulfilledCreatorCount',
        align: 'center',
        render: (value?: number) => formatCount(value)
      },
      {
        title: t('invitation.col_actions'),
        key: 'actions',
        render: (_, row) => (
          <div className='flex flex-wrap items-center gap-0.5'>
            {editableStatuses.has(row.collaborationStatus || '') ?
              <>
                <Button type='link' className='wb-text-link' onClick={() => openAddTalent(row)}>
                  {t('invitation.add_talent')}
                </Button>
                <Button type='link' className='wb-text-link' onClick={() => openAddProduct(row)}>
                  {t('invitation.add_product')}
                </Button>
              </>
              : null}
            <Button type='link' className='wb-text-link' onClick={() => openInviteDetail(row)}>
              {t('common.detail')}
            </Button>
          </div>
        )
      }
    ],
    [endTimeOrder, openAddProduct, openAddTalent, openInviteDetail, t]
  );
  const applyName = (value: string) => {
    setNameQuery(value.trim());
    setPageNum(1);
    setSelectedRowKeys([]);
  };
  const submitOptions = useMemo(
    () => submitStatusOptions.map((value) => ({ value, label: statusLabelOf(value, t) })),
    [t]
  );
  return (
    <div className='container'>
      <header className='mb-4'>
        <h1 className='mb-0 mt-0 text-[22px] font-semibold leading-snug tracking-tight text-slate-800'>{t('invitation.title')}</h1>
      </header>
      <section className='wb-section !p-0 overflow-hidden'>
        <div className='flex flex-wrap items-center gap-2 px-4 py-3 md:px-5'>
          <Button
            type='primary'
            className='wb-btn-primary rounded-lg'
            onClick={() => setCreateOpen(true)}>
            {t('invitation.create_target')}
          </Button>
          <Button className='rounded-lg' onClick={openCleanup}>
            {t('invitation.cleanup')}
          </Button>
          <Button className='rounded-lg' onClick={openMerge}>
            {t('invitation.merge_groups')}
          </Button>
          <Button className='rounded-lg'
            // disabled={selectedRowKeys.length === 0} 
            onClick={() => openAddTalent()}>
            {t('invitation.add_talent')}
          </Button>
          <Button
            className='rounded-lg'
            // disabled={selectedRowKeys.length === 0}
            onClick={() => openRemoveTalent()}>
            {t('invitation.remove_talent')}
          </Button>
          <Button className='rounded-lg'
            // disabled={selectedRowKeys.length === 0} 
            onClick={() => openAddProduct()}>
            {t('invitation.add_product')}
          </Button>
          {/* <span className='w-full text-xs leading-relaxed text-slate-400 md:ml-2 md:w-auto'>
            {t('invitation.kaloboost_hint')}
          </span> */}
        </div>
        <div className='flex flex-wrap items-center gap-2 border-t border-[#eef0f4] px-4 py-3 md:px-5'>
          <Input
            allowClear
            className='w-[220px] rounded-lg'
            placeholder={t('invitation.name_placeholder')}
            prefix={<SearchOutlined className='text-slate-400' />}
            value={nameInput}
            onChange={(event) => {
              const next = event.target.value;
              setNameInput(next);
              if (!next.trim()) applyName('');
            }}
            onPressEnter={() => applyName(nameInput)}
          />
          <Select
            allowClear
            className='min-w-[140px]'
            placeholder={t('invitation.submit_status')}
            value={submitStatus || undefined}
            options={submitOptions}
            onChange={(value: CollaborationSubmitStatus | undefined) => {
              setSubmitStatus(value ?? '');
              setPageNum(1);
              setSelectedRowKeys([]);
            }}
          />
        </div>
        <div className='wb-count-tabs border-t border-[#eef0f4] px-4 md:px-5' role='tablist' aria-label={t('invitation.status_tabs')}>
          {statusFilters.map((key) => {
            const active = statusFilter === key;
            return (
              <button
                key={key}
                type='button'
                role='tab'
                aria-selected={active}
                className={`wb-count-tabs-item${active ? ' wb-count-tabs-item--active' : ''}`}
                onClick={() => {
                  setStatusFilter(key);
                  setPageNum(1);
                  setSelectedRowKeys([]);
                }}>
                {filterLabels[key]}
              </button>
            );
          })}
        </div>
        <div className='px-4 pb-4 md:px-5'>
          <Table<TargetCollaboration>
            rowKey={(row) => rowKeyOf(row)}
            dataSource={records}
            loading={loading}
            pagination={{
              current: pageNum,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              onChange: (current, size) => {
                setSelectedRowKeys([]);
                if (size !== pageSize) {
                  setPageNum(1);
                  setPageSize(size);
                  return;
                }
                setPageNum(current);
              }
            }}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys
            }}
            onChange={(_pagination, _filters, sorter) => {
              const current = (Array.isArray(sorter) ? sorter[0] : sorter) as SorterResult<TargetCollaboration>;
              const next = current?.order === 'ascend' ? 'asc' : 'desc';
              if (next === endTimeOrder) return;
              setEndTimeOrder(next);
              setPageNum(1);
              setSelectedRowKeys([]);
            }}
            columns={columns}
          />
        </div>
      </section>
      <AddInviteTalentModal
        open={addTalentOpen}
        shopId={shopId}
        collaborationIds={talentInviteIds}
        onClose={() => {
          setAddTalentOpen(false);
          setTalentInviteIds([]);
        }}
        onSuccess={refreshList}
      />
      <RemoveInviteTalentModal
        open={removeTalentOpen}
        shopId={shopId}
        collaborationIds={removeInviteIds}
        onClose={() => {
          setRemoveTalentOpen(false);
          setRemoveInviteIds([]);
        }}
        onSuccess={refreshList}
      />
      <AddInviteProductModal
        open={addProductOpen}
        shopId={shopId}
        collaborationIds={productInviteIds}
        onClose={() => {
          setAddProductOpen(false);
          setProductInviteIds([]);
        }}
        onSuccess={refreshList}
      />
      <InviteDetailModal open={detailOpen} collaborationId={detailId} shopId={shopId} onClose={closeInviteDetail} />
      <MergeInviteGroupModal
        open={mergeOpen}
        shopId={shopId}
        invitations={mergeInvitations}
        onClose={() => {
          setMergeOpen(false);
          setMergeInvitations([]);
        }}
        onMerged={refreshList}
      />
      <CleanupInviteModal
        open={cleanupOpen}
        shopId={shopId}
        collaborationIds={cleanupInviteIds}
        onClose={() => {
          setCleanupOpen(false);
          setCleanupInviteIds([]);
        }}
        onCancelled={refreshList}
      />
      <CreateTargetInviteModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={refreshList}
      />
    </div>
  );
});
